import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CalendarDays, Clock, MapPin, CheckCircle2, Lock, ExternalLink, Phone } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import { fetchEventDetail, reserveEvent, sendOtp, verifyOtp, isAdminUser } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function HtmlFrame({ html, title }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(400);
  const resize = () => {
    try {
      const doc = ref.current?.contentWindow?.document;
      if (!doc?.body) return;
      doc.documentElement.style.minHeight = "0";
      doc.body.style.minHeight = "0";
      doc.body.style.margin = "0";
      const h = doc.body.scrollHeight;
      if (h) setHeight(h);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    const id = setInterval(resize, 600);
    const stop = setTimeout(() => clearInterval(id), 4000);
    return () => { clearInterval(id); clearTimeout(stop); };
  }, [html]);
  return (
    <iframe
      ref={ref}
      title={title || "event"}
      srcDoc={html}
      onLoad={resize}
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      className="w-full border-0 block"
      style={{ height }}
      data-testid="event-html-frame"
    />
  );
}

function isApproved(user) {
  if (!user) return false;
  if (isAdminUser(user)) return true;
  return user.approved === 1 || user.approved === true || String(user.approved || "") === "1";
}

function ReserveCard({ event, onReserved, dealerSlug }) {
  const { user, login, adoptUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [mode, setMode] = useState("email"); // email | phone
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [err, setErr] = useState("");

  const d = event.date_time ? new Date(event.date_time) : null;
  const full = event.spots_left != null && event.spots_left <= 0;

  const doReserve = async (recId) => {
    setBusy(true);
    setErr("");
    try {
      const res = await reserveEvent(event.slug, recId);
      setDone(true);
      onReserved(res.spots_left);
      toast.success("Your spot is reserved! 🎉");
      if (dealerSlug) {
        setTimeout(() => navigate(`/d/${dealerSlug}/events`), 1800);
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not reserve your spot.");
    } finally {
      setBusy(false);
    }
  };

  const reserveAsCurrent = () => {
    const recId = user?.user_record_id || user?.record_id;
    if (recId) doReserve(recId);
  };

  const signInEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await login(email.trim(), password);
      const recId = res?.user?.user_record_id || res?.user?.record_id;
      if (recId) await doReserve(recId);
    } catch (e2) {
      setErr(e2?.message || "Invalid email or password.");
      setBusy(false);
    }
  };

  const sendCode = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setBusy(true);
    setErr("");
    try {
      await sendOtp(phone.trim());
      setOtpSent(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't send the code.");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const data = await verifyOtp(phone.trim(), otp.trim());
      if (data?.success === false) { setErr("Invalid code."); setBusy(false); return; }
      if (data.signup_required) {
        setErr("No account found for this number. Please sign up first.");
        setBusy(false);
        return;
      }
      const sessionUser = data.user || data;
      adoptUser(sessionUser);
      if (!isApproved(sessionUser)) {
        setErr("Only approved users can make reservations.");
        setBusy(false);
        return;
      }
      await doReserve(sessionUser.user_record_id || sessionUser.record_id);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't verify the code.");
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden lg:sticky lg:top-24" data-testid="event-reserve-card">
      <div className="px-5 py-4 border-b border-zinc-100">
        <h2 className="text-lg font-bold text-zinc-900">Reserve Your Spot</h2>
      </div>
      <div className="p-5 space-y-4">
        {event.spots_left != null && (
          <div className={`rounded-xl px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5 ${full ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="event-spots">
            <CheckCircle2 size={15} />
            {full ? "Fully booked" : `${event.spots_left} of ${event.max_guests} spots left`}
          </div>
        )}
        <div className="rounded-xl bg-zinc-50 px-4 py-3">
          <div className="font-bold text-zinc-900">{d ? d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }) : event.date_label}</div>
          {d && <div className="text-sm text-zinc-500 mt-0.5">{d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" })}</div>}
        </div>

        {/* External event → link out */}
        {event.is_external && event.event_url ? (
          <a href={event.event_url} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold transition-colors" data-testid="event-external-btn">
            <ExternalLink size={16} /> Reserve on event site
          </a>
        ) : done ? (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-4 text-center" data-testid="event-reserved-confirm">
            <CheckCircle2 size={28} className="mx-auto text-emerald-600" />
            <p className="font-bold text-emerald-800 mt-2">You're booked!</p>
            <p className="text-xs text-emerald-700 mt-1">We've reserved your spot. See you there.</p>
            {dealerSlug && (
              <button onClick={() => navigate(`/d/${dealerSlug}/events`)} className="mt-3 inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold transition-colors" data-testid="event-back-to-dealer">
                ‹ Back to events
              </button>
            )}
          </div>
        ) : full ? (
          <button disabled className="w-full h-11 rounded-xl bg-zinc-100 text-zinc-400 text-sm font-bold cursor-not-allowed">Fully booked</button>
        ) : user ? (
          isApproved(user) ? (
            <button onClick={reserveAsCurrent} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold transition-colors" data-testid="event-reserve-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Reserve My Spot"}
            </button>
          ) : (
            <p className="text-sm text-zinc-500 text-center py-2">Only approved users can make reservations. Reach out to your sponsor to get approved.</p>
          )
        ) : (
          <div>
            <div className="flex items-center gap-1.5 text-sm font-bold text-zinc-800 mb-2">
              <Lock size={14} /> Sign In to Reserve
            </div>
            <p className="text-xs text-zinc-500 mb-3">Only approved users can make reservations.</p>
            <div className="flex rounded-lg bg-zinc-100 p-1 mb-3">
              <button onClick={() => { setMode("email"); setErr(""); }} className={`flex-1 text-xs font-semibold py-1.5 rounded-md ${mode === "email" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`} data-testid="reserve-mode-email">Email</button>
              <button onClick={() => { setMode("phone"); setErr(""); }} className={`flex-1 text-xs font-semibold py-1.5 rounded-md ${mode === "phone" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`} data-testid="reserve-mode-phone">Phone</button>
            </div>

            {mode === "email" ? (
              <form onSubmit={signInEmail} className="space-y-2">
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm" data-testid="reserve-email" required />
                <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm" data-testid="reserve-password" required />
                {err && <p className="text-xs text-rose-600" data-testid="reserve-error">{err}</p>}
                <button type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold" data-testid="reserve-signin-btn">
                  {busy ? "…" : "Sign In & Reserve"}
                </button>
              </form>
            ) : !otpSent ? (
              <form onSubmit={sendCode} className="space-y-2">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="Mobile number" className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm" data-testid="reserve-phone" required />
                {err && <p className="text-xs text-rose-600" data-testid="reserve-error">{err}</p>}
                <button type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold" data-testid="reserve-sendcode-btn">
                  {busy ? "Sending…" : "Text me a code"}
                </button>
              </form>
            ) : (
              <form onSubmit={verifyCode} className="space-y-2">
                <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm tracking-widest" data-testid="reserve-otp" required />
                {err && <p className="text-xs text-rose-600" data-testid="reserve-error">{err}</p>}
                <button type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold" data-testid="reserve-verify-btn">
                  {busy ? "Verifying…" : "Confirm & Reserve"}
                </button>
              </form>
            )}
            <p className="text-[11px] text-zinc-400 mt-3 text-center">
              No account? <Link to={`/login?next=${encodeURIComponent(`/events/${event.slug}`)}`} className="text-violet-700 font-semibold hover:underline">Create one</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p || "";
}

function HostCard({ host }) {
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5 flex items-center gap-4" data-testid="event-host-card">
      {host.logo && (
        <div className="shrink-0 h-20 w-20 rounded-xl bg-white border border-amber-200 overflow-hidden flex items-center justify-center">
          <img src={host.logo} alt={host.company} className="h-full w-full object-contain p-1" onError={(e) => (e.currentTarget.style.display = "none")} />
        </div>
      )}
      <div className="min-w-0">
        {host.company && <div className="text-lg font-bold text-amber-900 leading-tight">{host.company}</div>}
        {host.name && <div className="text-sm font-semibold text-amber-800 mt-1">{host.name}</div>}
        {host.address && <div className="text-sm text-amber-800/90 mt-1">{host.address}</div>}
        {host.phone && (
          <a href={`tel:${host.phone}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 mt-1.5 hover:underline" data-testid="event-host-phone">
            <Phone size={13} /> {fmtPhone(host.phone)}
          </a>
        )}
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const dealerSlug = params.get("d") || "";
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    fetchEventDetail(slug)
      .then((e) => {
        if (!alive) return;
        if (!e) setError("Event not found.");
        else { setEvent(e); document.title = `${e.title} · OreVital Events`; }
      })
      .catch(() => alive && setError("Could not load this event."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  const d = event?.date_time ? new Date(event.date_time) : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f7f4]" data-testid="event-detail-page">
      <PublicHeader active="events" nextPath={`/events/${slug}`} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-6">
        <Link to={dealerSlug ? `/d/${dealerSlug}/events` : "/events"} className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:underline mb-4" data-testid="event-back">
          ‹ Back to events
        </Link>
        {loading ? (
          <div className="py-24 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={18} /> Loading…</div>
        ) : error ? (
          <div className="py-20 text-center text-zinc-600" data-testid="event-error">{error}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            <div className="min-w-0 space-y-5">
              {event.image && (
                <div className="rounded-2xl overflow-hidden border border-zinc-200 bg-white shadow-sm" data-testid="event-banner">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full max-h-[340px] object-cover"
                    onError={(e) => (e.currentTarget.parentElement.style.display = "none")}
                  />
                </div>
              )}
              <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 tracking-tight">{event.title}</h1>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-zinc-600">
                  {d && <span className="inline-flex items-center gap-1.5"><span className="text-base leading-none">📅</span> {d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })}</span>}
                  {d && <span className="inline-flex items-center gap-1.5"><span className="text-base leading-none">⏱️</span> {d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" })}</span>}
                  {(event.city || event.company) && <span className="inline-flex items-center gap-1.5"><span className="text-base leading-none">📍</span> {[event.company, event.address, event.city, event.state].filter(Boolean).join(", ")}</span>}
                </div>
              </div>
              {event.host && <HostCard host={event.host} />}
              {event.description && (
                <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="event-description">
                  {/\<[a-z!][\s\S]*\>/i.test(event.description) ? (
                    <HtmlFrame html={event.description} title={event.title} />
                  ) : (
                    <div className="p-6 text-zinc-700 leading-relaxed whitespace-pre-wrap">{event.description}</div>
                  )}
                </div>
              )}
            </div>
            <ReserveCard event={event} dealerSlug={dealerSlug} onReserved={(left) => setEvent((e) => ({ ...e, spots_left: left }))} />
          </div>
        )}
      </main>
    </div>
  );
}
